// Tracking utility functions that respect user's tracking settings

interface TrackingSettings {
  googleAnalytics?: {
    enabled: boolean;
    trackPageViews: boolean;
    trackAddToCart: boolean;
    trackCheckout: boolean;
    trackPurchase: boolean;
    trackProductViews: boolean;
    trackCategoryViews: boolean;
    trackSearch: boolean;
    enhancedEcommerce: boolean;
  };
  facebookPixel?: {
    enabled: boolean;
    trackPageViews: boolean;
    trackAddToCart: boolean;
    trackCheckout: boolean;
    trackPurchase: boolean;
    trackProductViews: boolean;
    trackCategoryViews: boolean;
    trackSearch: boolean;
    trackLead: boolean;
  };
  googleTagManager?: {
    enabled: boolean;
    trackPageViews: boolean;
    trackAddToCart: boolean;
    trackCheckout: boolean;
    trackPurchase: boolean;
    trackProductViews: boolean;
    trackCategoryViews: boolean;
    trackSearch: boolean;
  };
}

// Get tracking settings from window object
function getTrackingSettings(): TrackingSettings {
  if (typeof window === 'undefined') return {};
  
  return {
    googleAnalytics: (window as any).gaTrackingSettings,
    facebookPixel: (window as any).fbTrackingSettings,
    googleTagManager: (window as any).gtmTrackingSettings,
  };
}

// Google Analytics tracking functions
export const trackGA = {
  pageView: (pageTitle?: string, pageLocation?: string) => {
    const settings = getTrackingSettings();
    if (!settings.googleAnalytics?.enabled || !settings.googleAnalytics.trackPageViews) return;
    
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('config', (window as any).gaTrackingSettings?.measurementId, {
        page_title: pageTitle,
        page_location: pageLocation || window.location.href,
      });
    }
  },

  addToCart: (product: any, quantity: number = 1) => {
    const settings = getTrackingSettings();
    if (!settings.googleAnalytics?.enabled || !settings.googleAnalytics.trackAddToCart) return;
    
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'add_to_cart', {
        currency: 'INR',
        value: product.price * quantity,
        items: [{
          item_id: product.id,
          item_name: product.name,
          category: product.category?.name || 'General',
          price: product.price,
          quantity: quantity
        }]
      });
    }
  },

  checkout: (step: number, products: any[], value: number) => {
    const settings = getTrackingSettings();
    if (!settings.googleAnalytics?.enabled || !settings.googleAnalytics.trackCheckout) return;
    
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'begin_checkout', {
        currency: 'INR',
        value: value,
        items: products.map(product => ({
          item_id: product.id,
          item_name: product.name,
          category: product.category?.name || 'General',
          price: product.price,
          quantity: product.quantity
        }))
      });
    }
  },

  purchase: (transactionId: string, products: any[], value: number, tax: number = 0, shipping: number = 0) => {
    const settings = getTrackingSettings();
    if (!settings.googleAnalytics?.enabled || !settings.googleAnalytics.trackPurchase) return;
    
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'purchase', {
        transaction_id: transactionId,
        value: value,
        tax: tax,
        shipping: shipping,
        currency: 'INR',
        items: products.map(product => ({
          item_id: product.id,
          item_name: product.name,
          category: product.category?.name || 'General',
          price: product.price,
          quantity: product.quantity
        }))
      });
    }
  },

  productView: (product: any) => {
    const settings = getTrackingSettings();
    if (!settings.googleAnalytics?.enabled || !settings.googleAnalytics.trackProductViews) return;
    
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'view_item', {
        currency: 'INR',
        value: product.price,
        items: [{
          item_id: product.id,
          item_name: product.name,
          category: product.category?.name || 'General',
          price: product.price
        }]
      });
    }
  },

  categoryView: (category: any) => {
    const settings = getTrackingSettings();
    if (!settings.googleAnalytics?.enabled || !settings.googleAnalytics.trackCategoryViews) return;
    
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'view_item_list', {
        item_list_name: category.name,
        items: category.products?.map((product: any) => ({
          item_id: product.id,
          item_name: product.name,
          category: category.name,
          price: product.price
        })) || []
      });
    }
  },

  search: (searchTerm: string) => {
    const settings = getTrackingSettings();
    if (!settings.googleAnalytics?.enabled || !settings.googleAnalytics.trackSearch) return;
    
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'search', {
        search_term: searchTerm
      });
    }
  }
};

// Facebook Pixel tracking functions
export const trackFB = {
  pageView: () => {
    const settings = getTrackingSettings();
    if (!settings.facebookPixel?.enabled || !settings.facebookPixel.trackPageViews) return;
    
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'PageView');
    }
  },

  addToCart: (product: any, quantity: number = 1) => {
    const settings = getTrackingSettings();
    if (!settings.facebookPixel?.enabled || !settings.facebookPixel.trackAddToCart) return;
    
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'AddToCart', {
        content_ids: [product.id],
        content_name: product.name,
        content_type: 'product',
        value: product.price * quantity,
        currency: 'INR',
        quantity: quantity
      });
    }
  },

  checkout: (step: number, products: any[], value: number) => {
    const settings = getTrackingSettings();
    if (!settings.facebookPixel?.enabled || !settings.facebookPixel.trackCheckout) return;
    
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'InitiateCheckout', {
        content_ids: products.map(p => p.id),
        content_name: products.map(p => p.name).join(', '),
        content_type: 'product',
        value: value,
        currency: 'INR',
        num_items: products.length
      });
    }
  },

  purchase: (transactionId: string, products: any[], value: number) => {
    const settings = getTrackingSettings();
    if (!settings.facebookPixel?.enabled || !settings.facebookPixel.trackPurchase) return;
    
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'Purchase', {
        content_ids: products.map(p => p.id),
        content_name: products.map(p => p.name).join(', '),
        content_type: 'product',
        value: value,
        currency: 'INR',
        num_items: products.length
      });
    }
  },

  productView: (product: any) => {
    const settings = getTrackingSettings();
    if (!settings.facebookPixel?.enabled || !settings.facebookPixel.trackProductViews) return;
    
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'ViewContent', {
        content_ids: [product.id],
        content_name: product.name,
        content_type: 'product',
        value: product.price,
        currency: 'INR'
      });
    }
  },

  categoryView: (category: any) => {
    const settings = getTrackingSettings();
    if (!settings.facebookPixel?.enabled || !settings.facebookPixel.trackCategoryViews) return;
    
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'ViewContent', {
        content_name: category.name,
        content_type: 'product_group',
        content_ids: category.products?.map((p: any) => p.id) || []
      });
    }
  },

  search: (searchTerm: string) => {
    const settings = getTrackingSettings();
    if (!settings.facebookPixel?.enabled || !settings.facebookPixel.trackSearch) return;
    
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'Search', {
        search_string: searchTerm
      });
    }
  },

  lead: () => {
    const settings = getTrackingSettings();
    if (!settings.facebookPixel?.enabled || !settings.facebookPixel.trackLead) return;
    
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'Lead');
    }
  }
};

// Google Tag Manager tracking functions
export const trackGTM = {
  pageView: (pageTitle?: string, pageLocation?: string) => {
    const settings = getTrackingSettings();
    if (!settings.googleTagManager?.enabled || !settings.googleTagManager.trackPageViews) return;
    
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'page_view',
        page_title: pageTitle,
        page_location: pageLocation || window.location.href
      });
    }
  },

  addToCart: (product: any, quantity: number = 1) => {
    const settings = getTrackingSettings();
    if (!settings.googleTagManager?.enabled || !settings.googleTagManager.trackAddToCart) return;
    
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: 'INR',
          value: product.price * quantity,
          items: [{
            item_id: product.id,
            item_name: product.name,
            category: product.category?.name || 'General',
            price: product.price,
            quantity: quantity
          }]
        }
      });
    }
  },

  checkout: (step: number, products: any[], value: number) => {
    const settings = getTrackingSettings();
    if (!settings.googleTagManager?.enabled || !settings.googleTagManager.trackCheckout) return;
    
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          currency: 'INR',
          value: value,
          items: products.map(product => ({
            item_id: product.id,
            item_name: product.name,
            category: product.category?.name || 'General',
            price: product.price,
            quantity: product.quantity
          }))
        }
      });
    }
  },

  purchase: (transactionId: string, products: any[], value: number) => {
    const settings = getTrackingSettings();
    if (!settings.googleTagManager?.enabled || !settings.googleTagManager.trackPurchase) return;
    
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'purchase',
        ecommerce: {
          transaction_id: transactionId,
          value: value,
          currency: 'INR',
          items: products.map(product => ({
            item_id: product.id,
            item_name: product.name,
            category: product.category?.name || 'General',
            price: product.price,
            quantity: product.quantity
          }))
        }
      });
    }
  },

  productView: (product: any) => {
    const settings = getTrackingSettings();
    if (!settings.googleTagManager?.enabled || !settings.googleTagManager.trackProductViews) return;
    
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'view_item',
        ecommerce: {
          currency: 'INR',
          value: product.price,
          items: [{
            item_id: product.id,
            item_name: product.name,
            category: product.category?.name || 'General',
            price: product.price
          }]
        }
      });
    }
  },

  categoryView: (category: any) => {
    const settings = getTrackingSettings();
    if (!settings.googleTagManager?.enabled || !settings.googleTagManager.trackCategoryViews) return;
    
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'view_item_list',
        ecommerce: {
          item_list_name: category.name,
          items: category.products?.map((product: any) => ({
            item_id: product.id,
            item_name: product.name,
            category: category.name,
            price: product.price
          })) || []
        }
      });
    }
  },

  search: (searchTerm: string) => {
    const settings = getTrackingSettings();
    if (!settings.googleTagManager?.enabled || !settings.googleTagManager.trackSearch) return;
    
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'search',
        search_term: searchTerm
      });
    }
  }
};

// Convenience function to track events across all enabled platforms
export const trackEvent = {
  pageView: (pageTitle?: string, pageLocation?: string) => {
    trackGA.pageView(pageTitle, pageLocation);
    trackFB.pageView();
    trackGTM.pageView(pageTitle, pageLocation);
  },

  addToCart: (product: any, quantity: number = 1) => {
    trackGA.addToCart(product, quantity);
    trackFB.addToCart(product, quantity);
    trackGTM.addToCart(product, quantity);
  },

  checkout: (step: number, products: any[], value: number) => {
    trackGA.checkout(step, products, value);
    trackFB.checkout(step, products, value);
    trackGTM.checkout(step, products, value);
  },

  purchase: (transactionId: string, products: any[], value: number, tax: number = 0, shipping: number = 0) => {
    trackGA.purchase(transactionId, products, value, tax, shipping);
    trackFB.purchase(transactionId, products, value);
    trackGTM.purchase(transactionId, products, value);
  },

  productView: (product: any) => {
    trackGA.productView(product);
    trackFB.productView(product);
    trackGTM.productView(product);
  },

  categoryView: (category: any) => {
    trackGA.categoryView(category);
    trackFB.categoryView(category);
    trackGTM.categoryView(category);
  },

  search: (searchTerm: string) => {
    trackGA.search(searchTerm);
    trackFB.search(searchTerm);
    trackGTM.search(searchTerm);
  },

  lead: () => {
    trackFB.lead();
  }
}; 